export const REPORT_IMAGE_ASPECT_RATIO = 4 / 7;

export interface ReportReadyCropPlacement {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type ReportReadyImagePreset = "none" | "grayscale" | "sepia";

export interface ReportReadyEditorAdjustments {
  rotation: number;
  brightness: number;
  contrast: number;
  preset: ReportReadyImagePreset;
}

export interface ReportReadySavedImage {
  reportCrop: ReportReadyCropPlacement;
  adjustments: ReportReadyEditorAdjustments;
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function coverCrop(imageWidth: number, imageHeight: number): ReportReadyCropPlacement {
  const imageAspect = imageWidth / imageHeight;
  if (imageAspect > REPORT_IMAGE_ASPECT_RATIO) {
    const width = REPORT_IMAGE_ASPECT_RATIO / imageAspect;
    return { x: (1 - width) / 2, y: 0, width, height: 1 };
  }

  const height = imageAspect / REPORT_IMAGE_ASPECT_RATIO;
  return { x: 0, y: (1 - height) / 2, width: 1, height };
}

export function cropZoom(crop: ReportReadyCropPlacement, baseCrop: ReportReadyCropPlacement): number {
  return clamp(baseCrop.width / crop.width, 1, 4);
}

export function applyCenterZoom(
  baseCrop: ReportReadyCropPlacement,
  multiplier: number,
): ReportReadyCropPlacement {
  const zoom = clamp(multiplier, 1, 4);
  const width = baseCrop.width / zoom;
  const height = baseCrop.height / zoom;
  return {
    x: clamp(baseCrop.x + (baseCrop.width - width) / 2, 0, 1 - width),
    y: clamp(baseCrop.y + (baseCrop.height - height) / 2, 0, 1 - height),
    width,
    height,
  };
}

export const DEFAULT_FRAME_ZOOM = 1.2;

export function initialReportCrop(imageWidth: number, imageHeight: number): ReportReadyCropPlacement {
  return applyCenterZoom(coverCrop(imageWidth, imageHeight), DEFAULT_FRAME_ZOOM);
}

export function effectiveImageSize(
  width: number,
  height: number,
  rotation: number,
): { width: number; height: number } {
  const normalized = ((rotation % 360) + 360) % 360;
  if (normalized === 90 || normalized === 270) {
    return { width: height, height: width };
  }
  return { width, height };
}

export function buildPreviewFilter(
  brightness: number,
  contrast: number,
  preset: ReportReadyImagePreset,
): string {
  const parts: string[] = [];
  if (preset === "grayscale") parts.push("grayscale(1)");
  if (preset === "sepia") parts.push("sepia(1)");
  parts.push(`brightness(${clamp(1 + brightness, 0.1, 3)})`);
  parts.push(`contrast(${clamp(1 + contrast, 0.1, 3)})`);
  return parts.join(" ");
}

export function normalizePreset(value: unknown): ReportReadyImagePreset {
  if (value === "grayscale" || value === "sepia") return value;
  return "none";
}
